import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, MapPin, Building2 } from "lucide-react";
import { getVisitors } from "@/lib/analyticsApi";
import { formatError } from "@/lib/api";
import { NEON, Card, Loading, Empty, Table, Th, Td, fmtMs, fmtNum, fmtDate } from "./shared";

const loc = (v) => {
  const parts = [v.ip_city, v.ip_region, v.ip_country].filter(Boolean);
  return parts.length ? parts.join(", ") : "Unknown";
};

const company = (v) => v.ip_org || v.ip_isp || "—";

const VisitorsView = ({ days }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getVisitors(days)
      .then((d) => { if (alive) setRows(Array.isArray(d) ? d : []); })
      .catch((e) => toast.error(formatError(e?.response?.data?.detail, "Failed to load visitors.")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading) return <Loading label="Loading visitors…" />;
  if (!rows || rows.length === 0) return <Empty label="No visitors recorded yet." />;

  const toggle = (vid) => setOpen((o) => ({ ...o, [vid]: !o[vid] }));

  return (
    <Card title={`Unique Visitors (${rows.length})`}>
      <Table>
        <thead>
          <tr>
            <Th />
            <Th>Last Seen</Th>
            <Th>Location</Th>
            <Th>Company / ISP</Th>
            <Th>Device</Th>
            <Th>Screen</Th>
            <Th>Views</Th>
            <Th>Engaged</Th>
            <Th>Referrer</Th>
            <Th>IP</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => {
            const isOpen = !!open[v.vid];
            const topPaths = v.top_paths || [];
            return (
              <React.Fragment key={v.vid}>
                <tr
                  className="hover:bg-white/5 cursor-pointer transition-colors"
                  onClick={() => toggle(v.vid)}
                >
                  <Td className="w-6 text-white/40">
                    {topPaths.length > 0
                      ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                      : null}
                  </Td>
                  <Td className="whitespace-nowrap text-white/70">{fmtDate(v.last_seen)}</Td>
                  <Td className="max-w-[180px]">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={12} className="text-[#2ad6ff] shrink-0" />
                      <span className="truncate">{loc(v)}</span>
                    </span>
                  </Td>
                  <Td className="max-w-[180px]">
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 size={12} className="text-[#ff2a8f] shrink-0" />
                      <span className="truncate">{company(v)}</span>
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap text-white/70">
                    {[v.browser, v.os, v.device_type].filter(Boolean).join(" · ") || "—"}
                  </Td>
                  <Td className="whitespace-nowrap text-white/60 font-mono text-[12px]">{v.screen || "—"}</Td>
                  <Td className="tabular-nums" style={{ color: NEON }}>{fmtNum(v.pageviews)}</Td>
                  <Td className="tabular-nums">{fmtMs(v.total_ms)}</Td>
                  <Td className="max-w-[160px] truncate text-white/55">{v.referrer || "direct"}</Td>
                  <Td className="font-mono text-[12px] text-white/55 whitespace-nowrap">{v.ip || "—"}</Td>
                </tr>
                {isOpen && topPaths.length > 0 && (
                  <tr>
                    <Td colSpan={10} className="bg-white/[0.03]">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-bold mb-2">
                        Top pages · {v.sessions || 0} session{(v.sessions || 0) === 1 ? "" : "s"} · {v.language || "—"}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {topPaths.map((p, i) => (
                          <span
                            key={i}
                            className="px-3 py-1.5 rounded-lg text-[12px] font-mono border border-white/12 bg-white/5"
                          >
                            <span className="text-white/80">{p.path}</span>
                            <span className="text-white/40 ml-2">{p.views}×</span>
                          </span>
                        ))}
                      </div>
                    </Td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};

export default VisitorsView;
